import { AppstoreOutlined, CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { ServiceGroupPickerDrawer } from '@components';
import { L } from '@lib';
import { ServiceGroup } from '@services';
import { useStore } from '@stores';
import { Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import React, { useEffect, useMemo, useState } from 'react';

const { Text } = Typography;

interface IProps {
  value?: number | number[];
  defaultValue?: { label: string; value: number };
  placeholder?: string;
  mode?: 'multiple' | 'tags';
  status?: 'error' | 'warning';
  stickyHeader?: boolean;
  onClear?: () => void;
  onSelect?: (id: number) => void;
  onDeselect?: (id: number) => void;
}

const triggerBaseStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: 32,
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  padding: '2px 28px 2px 10px',
  background: '#fff',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 4,
  cursor: 'pointer',
  position: 'relative',
  transition: 'border-color 0.15s',
};

const statusBorder = (status?: 'error' | 'warning') => {
  if (status === 'error') return '#ff4d4f';
  if (status === 'warning') return '#faad14';
  return undefined;
};

export const ServiceGroupSelect: React.FC<IProps> = observer(
  ({
    mode,
    value,
    defaultValue,
    placeholder = L('Service category select'),
    status,
    onClear,
    onSelect,
    onDeselect,
  }) => {
    const { serviceGroupStore } = useStore();
    const isMulti = mode === 'multiple' || mode === 'tags';

    const [open, setOpen] = useState(false);
    const [labelMap, setLabelMap] = useState<Record<number, string>>({});
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
      if (!defaultValue) return;
      const { label, value: defaultVal } = defaultValue;
      if (defaultVal == null || !label) return;
      setLabelMap((prev) => (prev[defaultVal] !== undefined ? prev : { ...prev, [defaultVal]: label }));
    }, [defaultValue]);

    const selectedIds = useMemo<number[]>(() => {
      if (value == null) return [];
      return Array.isArray(value) ? value.filter((v) => v != null) : [value];
    }, [value]);

    const getLabel = (id: number) => labelMap[id] ?? `#${id}`;

    const handleSelect = (group: ServiceGroup) => {
      setLabelMap((prev) => ({ ...prev, [group.id]: group.name }));
      runInAction(() => {
        serviceGroupStore.selectServiceGroups.set(group.id, group);
      });
      onSelect?.(group.id);
    };

    const handleDeselect = (id: number) => {
      onDeselect?.(id);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear?.();
    };

    const openDrawer = () => setOpen(true);

    const borderColor = statusBorder(status);
    const triggerStyle: React.CSSProperties = {
      ...triggerBaseStyle,
      borderColor: borderColor ?? (hovered ? '#4096ff' : '#d9d9d9'),
    };

    const showClear = selectedIds.length > 0;

    return (
      <>
        <div
          role="button"
          tabIndex={0}
          onClick={openDrawer}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openDrawer();
            }
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={triggerStyle}
        >
          {selectedIds.length === 0 && (
            <Text type="secondary" style={{ flex: 1, color: '#bfbfbf', fontSize: 14 }}>
              {placeholder}
            </Text>
          )}

          {selectedIds.length > 0 && !isMulti && (
            <Text style={{ flex: 1, fontSize: 14 }} ellipsis>
              {getLabel(selectedIds[0])}
            </Text>
          )}

          {selectedIds.length > 0 &&
            isMulti &&
            selectedIds.map((id) => (
              <Tag
                key={id}
                icon={<AppstoreOutlined />}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  handleDeselect(id);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  margin: '2px 0',
                  padding: '2px 8px',
                  background: '#f5f5f5',
                  border: '1px solid #bfbfbf',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#333',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {getLabel(id)}
              </Tag>
            ))}

          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#bfbfbf',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {showClear && hovered ? (
              <CloseCircleFilled
                onClick={handleClear}
                style={{ cursor: 'pointer', color: '#bfbfbf' }}
              />
            ) : (
              <DownOutlined />
            )}
          </span>
        </div>

        <ServiceGroupPickerDrawer
          open={open}
          mode={isMulti ? 'multiple' : 'single'}
          selectedIds={selectedIds}
          selectedLabels={labelMap}
          title={placeholder}
          onClose={() => setOpen(false)}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          onClear={onClear}
        />
      </>
    );
  }
);
